import { UserIcon, MapPinIcon, BanknotesIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import FormItem from "components/FormItem/FormItem";
import Label from "components/Label/Label";
import __cities from "data/jsons/cities.json";
import { LocationFormProps } from "data/reduxInterfaces";
import { useAppSelector } from "hooks/hooks";
import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import OnboardingTour, { stepStyle } from "utils/tours/OnboardingTour";
import { z } from "zod";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import toast from "react-hot-toast";
import { Card, List, ListItem } from "@material-tailwind/react";

const formSchema = z.object({
  bank: z.string({
    required_error: "Le compte bancaire est obligatoire.",
    invalid_type_error: "Le compte bancaire est obligatoire.",
  }),
  rib: z
    .string()
    .min(27, { message: "Le RIB doit contenir exactement 27 caractères." })
    .max(27, { message: "Le RIB doit contenir exactement 27 caractères." })
    .refine((value) => value.startsWith("FR"), {
      message: "Le RIB doit commencer par 'FR'.",
    }),

  city: z.string().min(1, { message: "Le ville est obligatoire." }),
  address: z.string().min(1, { message: 'L"addresse est obligatoire.' }),
  address2: z.string().optional(),
  country: z.string().min(1, { message: "Le Pays est obligatoire." }),
  codePostale: z.number({
    required_error: "Le code postale est obligatoire.",
    invalid_type_error: "Le code postale doit étre un nombre.",
  }),
  stripe_url: z.string().optional(),
});

const LocationParticularDetailedData: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  handleForm,
  data,
  errorLocationForm,
  FormData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      city: data?.city ?? "",
      address: data?.address ?? "",
      address2: data?.address2 ?? "",
      country: data?.country ?? "",
      codePostale: data?.codePostale ?? undefined,
      stripe_url: "eventtobee.com",
    },
    mode: "onSubmit",
  });
  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  const steps = [
    {
      selector: ".adresse",
      content: "Ensemble d'informations permettant d'indiquer le lieu de résidence.",
      stepStyle,
    },
    {
      selector: ".ville",
      content: "Région de logements éparpillés dans la campagne..",
      stepStyle,
    },

    {
      selector: ".code-postal",
      content: "Code facilitant le tri et l'acheminement automatique du courrier.",
      stepStyle,
    },

    {
      selector: ".bank ",
      content:
        "Établissement financier qui, en recevant des fonds du public, les utilise pour réaliser des opérations de crédit et d'autres activités financières. ",
    },
    {
      selector: ".iban ",
      content:
        "IBAN signifie « International Bank Account Number ». Il s'agit de l'identifiant international de votre compte bancaire .",
    },
  ];

  useEffect(() => {
    if (handleForm.type === "chekLocationFormFourteenParticular") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const updatelocationStates = useAppSelector((state) => state.location.locationData);
  useEffect(() => {
    if (updatelocationStates) {
      setValue("bank", updatelocationStates.bank);
      setValue("rib", updatelocationStates.rib);
      setValue("stripe_url", updatelocationStates.stripe_url);
    }
  }, [updatelocationStates]);

  const { placesService, placePredictions, isPlacePredictionsLoading, getPlacePredictions } = usePlacesService({
    apiKey: process.env.REACT_APP_MAPS_API_KEY + "&loading=async",
    options: {
      input: "",
      componentRestrictions: {
        country: "fr",
      },
    },
  });

  useEffect(() => {
    if (data) {
      setValue("city", data.city);
      setValue("address", data.address);
      setValue("address2", data.address2);
      setValue("codePostale", data.codePostale);
    }
  }, [data]);

  const [isload, setIload] = useState(isLoading);

  const holdeSelectAddress = async (place_id: string) => {
    setIload(true);
    placesService?.getDetails({ placeId: place_id }, (placeDetails: any, placeStatus: any) => {
      if (placeStatus !== "OK") {
        toast.error("Quelque chose ne fonctionne pas, veuillez réessayer!");
      }
      const parser = new DOMParser();
      const adr_address = parser.parseFromString(placeDetails?.adr_address ?? "", "text/html");
      const streetAddress = adr_address.querySelector(".street-address")?.textContent || "";
      const postalCode = adr_address.querySelector(".postal-code")?.textContent || "";
      const locality = adr_address.querySelector(".locality")?.textContent || "";
      const country_name = adr_address.querySelector(".country-name")?.textContent || "";

      if (streetAddress !== placeDetails?.name) {
        setValue("address", `${placeDetails?.name}, ${streetAddress}`);
      } else {
        setValue("address", streetAddress);
      }
      setValue("codePostale", Number(postalCode));
      setValue("city", locality);
      setValue("country", country_name);

      setIload(false);
    });

    getPlacePredictions({ input: "" });
  };

  return (
    <div className={className}>
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">📝 Informations personnelles</h2>
        <p className="text-gray-600 dark:text-gray-400">Complétez vos informations personnelles et bancaires</p>
        <div className="border-b-2 w-20 border-eventToBee mt-4 mx-auto"></div>
      </div>

      <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
        {/* Section Adresse personnelle */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-6">
            <MapPinIcon className="w-6 h-6 text-eventToBee mr-3" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Adresse personnelle</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="adresse md:col-span-2">
              <Label>Adresse *</Label>
              <Input
                className={`mt-1.5 ${errors.address?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Votre adresse"
                {...register("address")}
                onChange={(e) => {
                  setValue("address", e.target.value);
                  getPlacePredictions({ input: e.target.value });
                }}
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    getPlacePredictions({ input: "" });
                  }
                }}
                loading={isPlacePredictionsLoading || isload}
              />

              {placePredictions.length > 0 && (
                <div className="relative z-10 mt-1">
                  <Card className="w-full">
                    <List>
                      {placePredictions.map((item, index) => (
                        <ListItem key={index} onClick={() => holdeSelectAddress(item.place_id)}>
                          {item.description}
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </div>
              )}

              {errors.address?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.address?.message}</div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>Complément d'adresse</Label>
              <Input
                disabled={isLoading || isload}
                placeholder="Étage, appartement, etc."
                {...register("address2")}
              />
            </div>

            <div className="ville">
              <Label>Ville *</Label>
              <Input
                className={`mt-1.5 ${errors.city?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Ville"
                {...register("city")}
              />
              {errors.city?.message && <div className="text-xs text-red-500 mt-1">{errors.city?.message}</div>}
            </div>

            <div className="code-postal">
              <Label>Code postal *</Label>
              <Input
                className={`mt-1.5 ${errors.codePostale?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Code postal"
                type="number"
                {...register("codePostale", { valueAsNumber: true })}
              />
              {errors.codePostale?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.codePostale?.message}</div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>Pays *</Label>
              <Input
                className={`mt-1.5 ${errors.country?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="France"
                {...register("country")}
              />
              {errors.country?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.country?.message}</div>
              )}
            </div>
          </div>
        </div>

        {/* Section Informations bancaires */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-6">
            <BanknotesIcon className="w-6 h-6 text-eventToBee mr-3" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Informations bancaires</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bank">
              <Label>Code BIC *</Label>
              <Input
                {...register("bank")}
                disabled={isLoading}
                type="text"
                placeholder="Code BIC de votre banque"
                className={`mt-1.5 ${errors.bank?.message ? "border-red-500" : ""}`}
              />
              {errors.bank?.message && <div className="text-xs text-red-500 mt-1">{errors.bank?.message}</div>}
            </div>

            <div className="iban">
              <Label>IBAN (RIB) *</Label>
              <Input
                {...register("rib")}
                disabled={isLoading}
                type="text"
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                className={`mt-1.5 ${errors.rib?.message ? "border-red-500" : ""}`}
              />
              {errors.rib?.message && <div className="text-xs text-red-500 mt-1">{errors.rib?.message}</div>}
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-blue-500 rounded-full mt-0.5 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  Informations bancaires sécurisées
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Ces informations sont nécessaires pour recevoir vos paiements. Elles sont stockées de manière
                  sécurisée et chiffrée.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      <OnboardingTour steps={steps} page_name={"ParticularDetailedDataForm"}></OnboardingTour>
    </div>
  );
};

export default LocationParticularDetailedData;
